import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MonitorPlay, Fullscreen, PowerOff, Smartphone } from 'lucide-react';
import { AcMediaPlayer } from '../../components/security/AcMediaPlayer';

export function PublicTvPage() {
  const [searchParams] = useSearchParams();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExitHint, setShowExitHint] = useState(false);
  
  // Param: ?requireF11=false will bypass the F11 requirement
  const requireF11 = searchParams.get('requireF11') !== 'false';

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      
      if (isFull) {
        setShowExitHint(true);
        setTimeout(() => setShowExitHint(false), 7000);
      } else {
        setShowExitHint(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Allow F11 to naturally trigger fullscreen or force it if needed
      if (e.key === 'F11') {
        if (!document.fullscreenElement) {
          e.preventDefault();
          document.documentElement.requestFullscreen().catch(() => {});
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);

    // If requireF11 is false, try to enter fullscreen automatically
    if (!requireF11) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true); // Force state if auto fails due to browser policy, we still show the player
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [requireF11]);

  // Request fullscreen programmatically when the button is clicked
  const handleStartTv = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div 
      onClick={!isFullscreen ? handleStartTv : undefined}
      style={{ 
        width: '100vw', height: '100vh', 
        background: '#020617', overflow: 'hidden', 
        position: 'relative', fontFamily: 'Inter, sans-serif',
        cursor: !isFullscreen ? 'pointer' : 'default'
      }}
    >
      
      {/* Overlay de Orientação (Apenas Portrait Mobile) */}
      <div className="portrait-overlay" style={{
        position: 'fixed', inset: 0, background: '#000', zIndex: 9999,
        display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, color: '#fff', textAlign: 'center', padding: 24
      }}>
        <div style={{ animation: 'rotatePhone 2s ease-in-out infinite alternate', transformOrigin: 'center center' }}>
          <Smartphone size={80} color="#3b82f6" strokeWidth={1.5} />
        </div>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.05em', margin: 0 }}>
          Deite o Celular
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: 300, lineHeight: 1.5, margin: 0 }}>
          Para a melhor experiência da TV Ao Vivo, vire seu dispositivo para a horizontal.
        </p>
      </div>

      {/* Estado: Esperando F11 */}
      {requireF11 && !isFullscreen && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 24 }}>
          <div 
            onClick={handleStartTv}
            style={{
              background: 'rgba(15,23,42,0.8)',
              border: '1px solid rgba(59,130,246,0.3)',
              padding: '40px 60px',
              borderRadius: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
              transition: 'transform 0.2s',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
              <Fullscreen size={40} />
            </div>
            <h1 style={{ color: '#fff', fontSize: '2rem', fontWeight: 900, margin: 0 }}>Ligar TV</h1>
            <p style={{ color: '#94a3b8', fontSize: '1.2rem', margin: 0 }}>
              Pressione <strong style={{ color: '#fff', background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>F11</strong> ou clique para iniciar em Tela Cheia
            </p>
          </div>
        </div>
      )}

      {/* Estado: TV Ligada (Full Player) */}
      {(!requireF11 || isFullscreen) && (
        <div style={{ width: '100%', height: '100%', animation: 'fadeIn 1s ease' }}>
          <AcMediaPlayer mode="full" />
        </div>
      )}

      {/* Hint para sair do Fullscreen */}
      {showExitHint && (
        <div style={{
          position: 'absolute', top: 32, left: '50%', transform: 'translateX(-50%)',
          background: '#fff', padding: '12px 24px', borderRadius: 100,
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          zIndex: 100,
          animation: 'slideDownOut 7s forwards'
        }}>
          <PowerOff size={18} color="#000" />
          <span style={{ color: '#000', fontWeight: 800, fontSize: '0.95rem' }}>
            Pressione <strong style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>ESC</strong> para Desligar
          </span>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideDownOut {
          0% { top: -50px; opacity: 0; }
          10% { top: 32px; opacity: 1; }
          80% { top: 32px; opacity: 1; }
          100% { top: -50px; opacity: 0; }
        }
        @keyframes rotatePhone {
          0% { transform: rotate(0deg); }
          20% { transform: rotate(0deg); }
          80% { transform: rotate(90deg); }
          100% { transform: rotate(90deg); }
        }
        @media (max-width: 800px) and (orientation: portrait) {
          .portrait-overlay {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
}
